import { ref } from 'vue';
import { useLoading } from '@sa/hooks';
import type { Api } from '@/typings/api';
import type { BasicForm } from '@/typings/basic-form';
import { nanoid } from '~/packages/utils/src';

type PageFetchParam<T extends BasicForm.BaseData> = {
  /**
   * 查询字段
   */
  column: string[];
  /**
   * 查询过滤条件
   */
  filter?: Api.Common.Filter[];
  /**
   * 排序字段
   */
  order?: Api.Common.Order[];
  /**
   * 分页查询函数
   */
  fetchFun: Api.CommonMethods.FindPage<T>;

  /**
   * 分页大小, 默认 20
   */
  pageSize?: number;
  /**
   * 数据转换函数
   */
  transformer?: (data: T[]) => T[];
};
export function usePageFetch<T extends BasicForm.BaseData>(params: PageFetchParam<T>) {
  const pageData = ref<T[]>([]);
  const page = ref<Api.Common.Pageable>({
    page: 0,
    size: params.pageSize || 20,
    total: 0,
    last: false
  });
  const LastRequestId = ref<string>('');
  const { loading, startLoading, endLoading } = useLoading();
  async function getData() {
    startLoading();
    const requestId = nanoid();
    LastRequestId.value = requestId;
    const res = await params.fetchFun({
      column: params.column,
      filter: params.filter || [],
      order: params.order || [],
      pageable: page.value
    });
    if (requestId === LastRequestId.value) {
      const resData = res.data as Api.Common.PageQueryResult<T>;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      pageData.value.push(...(transformData(resData.content || []) as T[]));
      page.value.page = resData.number;
      page.value.total = resData.totalElements;
      page.value.last = resData.last;
      endLoading();
      return resData;
    }
    return null;
  }

  function transformData(data: T[]): T[] {
    return params.transformer?.(data) || data;
  }

  async function getNextPage() {
    if (page.value.last) {
      return;
    }
    page.value.page += 1;
    await getData();
  }

  async function refresh() {
    pageData.value = [];
    page.value.page = 0;
    await getData();
  }

  return {
    pageData,
    page,
    loading,
    getData,
    refresh,
    getNextPage
  };
}
